// Dynamic office coordinate caching system
interface OfficeCoordinate {
  lat: number
  lng: number
  name: string
  lastUpdated: number
}

const CACHE_DURATION = 24 * 60 * 60 * 1000 // 24 hours
const CACHE_KEY = 'office-coordinates-cache'

export class OfficeCoordinateCache {
  private cache: Map<string, OfficeCoordinate> = new Map()
  private pendingRequests: Map<string, Promise<OfficeCoordinate | null>> = new Map()

  constructor() {
    this.loadCache()
  }

  private loadCache() {
    // Only load cache on client side
    if (typeof window === 'undefined') return

    try {
      const stored = localStorage.getItem(CACHE_KEY)
      if (stored != null && stored !== '') {
        const data = JSON.parse(stored)
        this.cache = new Map(Object.entries(data))
      }
    } catch (error) {
      console.warn('Failed to load coordinate cache:', error)
    }
  }

  private saveCache() {
    // Only save cache on client side
    if (typeof window === 'undefined') return

    try {
      const data = Object.fromEntries(this.cache)
      localStorage.setItem(CACHE_KEY, JSON.stringify(data))
    } catch (error) {
      console.warn('Failed to save coordinate cache:', error)
    }
  }

  get(postcode: string): OfficeCoordinate | null {
    const cached = this.cache.get(postcode)
    if (!cached) return null

    // Check if cache is expired
    if (Date.now() - cached.lastUpdated > CACHE_DURATION) {
      this.cache.delete(postcode)
      this.saveCache()
      return null
    }

    return cached
  }

  set(postcode: string, coordinate: Omit<OfficeCoordinate, 'lastUpdated'>) {
    this.cache.set(postcode, {
      ...coordinate,
      lastUpdated: Date.now(),
    })
    this.saveCache()
  }

  async geocodePostcode(postcode: string, country: string): Promise<OfficeCoordinate | null> {
    // Check cache first
    const cached = this.get(postcode)
    if (cached) return cached

    // Check if there's already a pending request for this postcode
    const cacheKey = `${postcode}-${country}`
    const pendingRequest = this.pendingRequests.get(cacheKey)
    if (pendingRequest) {
      return pendingRequest
    }

    // Create new request and store it to prevent duplicates
    const requestPromise = this.doGeocodeRequest(postcode, country)
    this.pendingRequests.set(cacheKey, requestPromise)

    try {
      const result = await requestPromise
      return result
    } finally {
      // Clean up pending request after completion
      this.pendingRequests.delete(cacheKey)
    }
  }

  private async doGeocodeRequest(
    postcode: string,
    country: string
  ): Promise<OfficeCoordinate | null> {
    try {
      // Use our API route instead of calling Google directly
      const response = await fetch(
        `/api/geocode?postcode=${encodeURIComponent(postcode)}&country=${encodeURIComponent(country)}`
      )

      if (response.ok) {
        const data = await response.json()
        if (data.error) {
          console.warn(`Geocode error for ${postcode}:`, data.error)
          return null
        }

        if (data.lat && data.lng) {
          const coordinate = {
            lat: data.lat,
            lng: data.lng,
            name: data.name || postcode,
          }

          // Cache the result
          this.set(postcode, coordinate)
          return { ...coordinate, lastUpdated: Date.now() }
        }
      } else if (response.status === 429) {
        console.warn(`Rate limit exceeded for geocoding ${postcode}`)
      }
    } catch (error) {
      console.warn(`Failed to geocode ${postcode}:`, error)
    }

    return null
  }

  clear() {
    this.cache.clear()
    localStorage.removeItem(CACHE_KEY)
  }
}

// Initialize cache only on client side
let _officeCoordinateCache: OfficeCoordinateCache | null = null

export const getOfficeCoordinateCache = (): OfficeCoordinateCache => {
  if (!_officeCoordinateCache) {
    _officeCoordinateCache = new OfficeCoordinateCache()
  }
  return _officeCoordinateCache
}
